import request from "./request";
import promiseWithTimeout from "../helpers/promiseWithTimeout";
import { getContext } from "../client/context";
import logger from "../helpers/logger";
import {
  getSuggestionsUrl,
  getCountryUrl,
  getPaymentProviderUrl,
  getWelcomeMessageUrl,
  getPreferencesUrl,
  getContentApiUrl,
  getCustomerProfileUrl,
  getDeliveryOptionsUrl
} from "./apiUrls";
import sdkInstanceProvider from "@src/client/sdk/sdkInstanceProvider.js";
import { asosGlobalNamespace, sdkGlobalNamespace } from "../template/constants";
import { ASOS_HEADERS_PLAT, ASOS_HEADERS_NAME } from "./constants";
import clientConfig from "../template/clientConfig";
import env from "../env";
import "isomorphic-fetch";

const getIsRunningOnServer = () => clientConfig.isServer();

export { default as fetchClientNavigation } from "./fetchClientNavigation";
export { default as fetchNavigation } from "./fetchNavigation";

export const fetchSuggestions = (storeCode, lang, countryCode, query) => {
  const url = getSuggestionsUrl(storeCode, lang, countryCode, query);

  logger.info(`(${storeCode}) - Fetching navigation from ${url}`);
  return request(url);
};

export const fetchCountry = (country, lang, platform) => {
  const url = getCountryUrl(country, lang, platform);
  logger.info(`Fetching country data for ${country} from ${url}`);
  return request(url);
};

export const fetchPaymentProviders = keyStoreDataversion => {
  const url = getPaymentProviderUrl({ keyStoreDataversion });
  logger.info(`Fetching payment providers from ${url}`);
  return request(url);
};

export const fetchDefaultDeliveryCountry = async customerId => {
  const url = getCustomerProfileUrl(customerId, {
    fields: "deliveryCountry"
  });

  const { getAccessToken } = await sdkInstanceProvider("identity");
  const token = await getAccessToken();

  const headers = {
    "asos-c-name": "asos-web-site-chrome",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };

  return request(url, { headers });
};

export const getDeliveryOptions = (defaultDeliveryCountry, store) => {
  const deliveryOptionsUrl = getDeliveryOptionsUrl(defaultDeliveryCountry, {
    store
  });

  const headers = {
    "asos-c-name": "asos-web-site-chrome",
    "Content-Type": "application/json"
  };

  logger.info(
    `Fetching delivery options with ${defaultDeliveryCountry} and ${store} from ${deliveryOptionsUrl}`
  );

  return request(deliveryOptionsUrl, { headers });
};

export const fetchWelcomeMessage = (
  geoCountry,
  browseCountry,
  platform,
  deliveryCountry
) => {
  const url = getWelcomeMessageUrl(
    geoCountry,
    browseCountry,
    platform,
    deliveryCountry
  );

  const headers = {
    "asos-c-name": "asos-web-site-chrome",
    "Content-Type": "application/json"
  };

  logger.info(
    `Fetching welcome message for geoCountry "${geoCountry}", browseCountry "${browseCountry}", and deliveryCountry "${deliveryCountry}" from ${url}`
  );

  return request(url, { headers });
};

export const fetchTargetedContent = (parameters, context) => {
  const fetchUrl = getContentApiUrl(parameters, context);
  const clientContext = getContext();
  const buildVersion = clientContext && clientContext.getBuildVersion();
  const method = "GET";
  const credentials = "same-origin";
  const headers = {
    "asos-c-name": ASOS_HEADERS_NAME,
    "asos-c-plat": ASOS_HEADERS_PLAT,
    "asos-c-ver": buildVersion
  };

  if (!fetchUrl) {
    logger.error(`Content Targeting Fetch URL is not defined`);
    return Promise.reject("Content Targeting Fetch URL is not defined");
  }

  logger.info(`Fetching targeted content from ${fetchUrl}`);
  return promiseWithTimeout(
    request(fetchUrl, { method, credentials, headers }),
    8000,
    "Content Api is not responding"
  );
};

export const fetchSitecoreBearerToken = () => {
  const {
    SITECORE_LOGIN_URL: url,
    SITECORE_LOGIN_USER: username,
    SITECORE_LOGIN_PASS: password,
    SITECORE_LOGIN_DOMAIN: domain
  } = process.env;
  const body = JSON.stringify({
    username,
    password,

    domain
  });
  const method = "POST";
  const headers = {
    "Content-Type": "application/json"
  };
  return request(url, { method, headers, body });
};

export async function updatePreferences(customerGuid, preferences) {
  const url = getPreferencesUrl(customerGuid);
  logger.info(
    `Patching preferences for customer '${customerGuid}' from '${url}'`
  );
  const body = JSON.stringify({
    sourceReason: "SiteChrome",
    services: {
      marketing: preferences
    }
  });
  const method = "PATCH";
  const identitySdk = window[asosGlobalNamespace][sdkGlobalNamespace].identity;
  const headers = {
    "asos-c-name": "asos-web-site-chrome",
    "Content-Type": "application/json",
    Authorization: "Bearer " + (await identitySdk.getAccessToken())
  };

  return fetch(url, {
    method,
    headers,
    body
  });
}

export const fetchPreferences = (country, lang) => {
  const url = getPreferencesUrl(null, {
    lang,
    country
  });
  logger.info(`Fetching preferences for system from '${url}'`);

  return request(url);
};

export const fetchFeaturesManifest = () => {
  return request(env("FEATURES_MANIFEST_JSON"));
};

export const fetchTemplateFromBlobStorage = (
  siteChromeVersion,
  platform,
  countryCode
) => {
  const url = env("REDIS_WILDCARD").replace(
    "*",
    `${siteChromeVersion}-platform=${platform}&country=${countryCode}`
  );

  return fetch(url).then(response => response.text());
};

export const sendEventToAlfred = (event, url) => {
  const method = "POST";
  const headers = {
    "Content-Type": "application/json"
  };

  const body = JSON.stringify(event);
  return request(url, { method, headers, body }, getIsRunningOnServer()).catch(
    e => {
      console.error("Alfred event failed", e);
    }
  );
};
